<template>
    <div class="container-fluid py-2">
        <div class="row g-3">
            <!-- Left: Calendar Widget Card -->
            <div class="col-xl-4 col-lg-5">
                <div class="card task-calendar-card shadow-sm">
                    <!-- Calendar Header -->
                    <div class="card-header task-calendar-card-header">
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="d-flex align-items-center gap-2">
                                <span class="cal-header-icon">
                                    <i class="bi bi-calendar3"></i>
                                </span>
                                <div>
                                    <h6 class="mb-0 fw-bold cal-month-title">{{ formattedMonthYear }}</h6>
                                    <small class="text-muted" style="font-size: 0.76rem;">Aktivlik və icra təqvimi</small>
                                </div>
                            </div>
                            <div class="d-flex align-items-center gap-1">
                                <button type="button" class="cal-nav-btn" @click="prevMonth" title="Əvvəlki ay">
                                    <i class="bi bi-chevron-left"></i>
                                </button>
                                <button type="button" class="cal-today-btn" @click="goToday" title="Bu günə qayıt">
                                    Bugün
                                </button>
                                <button type="button" class="cal-nav-btn" @click="nextMonth" title="Növbəti ay">
                                    <i class="bi bi-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Calendar Body -->
                    <div class="card-body p-2 pt-0">
                        <el-config-provider :locale="azLocale">
                            <el-calendar ref="calendarRef" v-model="day">
                                <template #date-cell="{ data }">
                                    <div class="cal-cell-content" :class="{ 'is-selected-cell': isSelectedDay(data.day), 'is-today-cell': isToday(data.day) }">
                                        <span class="cal-day-number">{{ data.day.split('-')[2] }}</span>
                                        <span v-if="progress[data.day] !== undefined"
                                              class="cal-kpi-chip"
                                              :class="getKpiClass(progress[data.day])"
                                              :title="progress[data.day] + '% icra faizi'">
                                            {{ progress[data.day] }}%
                                        </span>
                                    </div>
                                </template>
                            </el-calendar>
                        </el-config-provider>

                        <!-- Selected Date Stats Summary Box -->
                        <div class="cal-selected-summary">
                            <div class="d-flex align-items-center justify-content-between mb-2">
                                <div class="d-flex align-items-center gap-2">
                                    <i class="bi bi-clock-history text-info"></i>
                                    <span class="fw-bold cal-summary-date">{{ formattedSelectedDate }}</span>
                                </div>
                                <span class="cal-task-count-badge">
                                    {{ filteredTasks.length }} tapşırıq
                                </span>
                            </div>
                            <div class="cal-stats-grid">
                                <div class="cal-stat-card stat--completed">
                                    <span class="stat-value text-success">{{ totalCompleted }}</span>
                                    <span class="stat-label">Tamamlandı</span>
                                </div>
                                <div class="cal-stat-card stat--pending">
                                    <span class="stat-value text-primary">{{ totalPending }}</span>
                                    <span class="stat-label">Gözləmədə</span>
                                </div>
                                <div class="cal-stat-card stat--rejected">
                                    <span class="stat-value text-danger">{{ totalRejected }}</span>
                                    <span class="stat-label">Rədd edildi</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right: Task Table Card -->
            <div class="col-xl-8 col-lg-7">
                <div class="card task-table-card shadow-sm">
                    <!-- Table Header with Unified Actions, Divider & Animated Status Rail -->
                    <div class="card-header task-table-card-header pb-3">
                        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                            <div class="d-flex align-items-center gap-2">
                                <span class="cal-header-icon cal-header-icon--blue">
                                    <i class="bi bi-list-check"></i>
                                </span>
                                <div>
                                    <h5 class="mb-0 fw-bold">Tapşırıqlar siyahısı</h5>
                                    <small class="text-muted" style="font-size: 0.78rem;">Planlaşdırılmış görüş və zənglər</small>
                                </div>
                            </div>

                            <div class="task-search-box">
                                <el-input
                                    v-model="searchQuery"
                                    placeholder="Şəxs, şirkət və ya qeyd..."
                                    clearable
                                    class="task-quick-search"
                                >
                                    <template #prefix>
                                        <i class="bi bi-search text-muted"></i>
                                    </template>
                                </el-input>
                            </div>
                        </div>

                        <!-- Unified Toolbar Line: Actions + Divider + Animated Filter Rail -->
                        <div class="task-toolbar-row">
                            <button type="button" @click="openNewDialog" class="btn action-btn action-btn--success">
                                <i class="bi bi-plus-lg me-1"></i>
                                <span>Yeni tapşırıq</span>
                            </button>
                            <button type="button" class="btn action-btn action-btn--muted" @click="refreshTasks">
                                <i class="bi bi-arrow-clockwise me-1" :class="{ 'spin-anim': isRefreshing }"></i>
                                <span>Yeniləmək</span>
                            </button>

                            <div class="toolbar-v-divider"></div>

                            <!-- Modern Animated Status Rail -->
                            <div class="modern-status-rail">
                                <button
                                    type="button"
                                    class="status-rail-item"
                                    :class="{ 'is-active': filterValue === '' }"
                                    @click="filterValue = ''"
                                >
                                    <i class="bi bi-grid-fill status-icon anim-pop"></i>
                                    <span>Hamısı</span>
                                    <span class="status-count-badge">{{ dataTask.length }}</span>
                                </button>

                                <button
                                    type="button"
                                    class="status-rail-item status--success"
                                    :class="{ 'is-active': filterValue === 'Tamamlandı' }"
                                    @click="filterValue = 'Tamamlandı'"
                                >
                                    <i class="bi bi-check-circle-fill status-icon anim-pulse"></i>
                                    <span>Tamamlandı</span>
                                    <span class="status-count-badge count--success">{{ totalCompleted }}</span>
                                </button>

                                <button
                                    type="button"
                                    class="status-rail-item status--primary"
                                    :class="{ 'is-active': filterValue === 'Gözləmədə' }"
                                    @click="filterValue = 'Gözləmədə'"
                                >
                                    <i class="bi bi-hourglass-split status-icon anim-spin-slow"></i>
                                    <span>Gözləmədə</span>
                                    <span class="status-count-badge count--primary">{{ totalPending }}</span>
                                </button>

                                <button
                                    type="button"
                                    class="status-rail-item status--danger"
                                    :class="{ 'is-active': filterValue === 'Rədd edildi' }"
                                    @click="filterValue = 'Rədd edildi'"
                                >
                                    <i class="bi bi-x-circle-fill status-icon anim-shake"></i>
                                    <span>Rədd edildi</span>
                                    <span class="status-count-badge count--danger">{{ totalRejected }}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Table Body -->
                    <div class="card-body p-0" v-loading="loading">
                        <div class="table-responsive">
                            <el-table border stripe :data="filteredTasks" class="task-modern-table" style="width: 100%">
                                <!-- Action: Edit (widened to 58px) -->
                                <el-table-column width="58" align="center">
                                    <template #default="scope">
                                        <el-button class="edit-btn"
                                            @click="editTask(scope.row)"
                                            style="color: #4db2e5; padding: 0 !important;"
                                            :icon="Edit"
                                            text
                                        />
                                    </template>
                                </el-table-column>

                                <!-- Tarix / Vaxt (widened to 115px to prevent truncation dots) -->
                                <el-table-column prop="taskDate" label="Tarix" width="115" align="center">
                                    <template #default="scope">
                                        <span class="task-time-chip">
                                            <i class="bi bi-clock me-1"></i>{{ scope.row.taskDate }}
                                        </span>
                                    </template>
                                </el-table-column>

                                <!-- Şirkət / Şəxs -->
                                <el-table-column label="Şirkət / Şəxs" min-width="210">
                                    <template #default="scope">
                                        <div class="d-flex align-items-center gap-2">
                                            <div class="person-avatar" :style="getAvatarStyle(scope.row.personName)">
                                                {{ getInitials(scope.row.personName) }}
                                            </div>
                                            <div class="d-flex flex-column text-truncate">
                                                <span class="person-name text-truncate">{{ scope.row.personName }}</span>
                                                <span class="company-name text-truncate">
                                                    <i class="bi bi-building me-1"></i>{{ scope.row.companyName }}
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                </el-table-column>

                                <!-- Qeydlər -->
                                <el-table-column prop="descr" label="Qeydlər" min-width="220">
                                    <template #default="scope">
                                        <span class="task-note-text">{{ scope.row.descr }}</span>
                                    </template>
                                </el-table-column>

                                <!-- Tapşırıq növü -->
                                <el-table-column label="Tapşırıq növü" width="145">
                                    <template #default="scope">
                                        <div class="d-flex flex-column gap-1">
                                            <div class="d-flex align-items-center gap-1 task-type-label">
                                                <i :class="getTaskTypeIcon(scope.row.taskType)" class="task-type-icon"></i>
                                                <span>{{ scope.row.taskType }}</span>
                                            </div>
                                            <div>
                                                <span class="badge-status-dot" :class="scope.row.online">
                                                    <span class="dot"></span>
                                                    <span>{{ scope.row.online === 'online' ? 'Online' : 'Offline' }}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                </el-table-column>

                                <!-- Nəticə (Status Badge Pill) -->
                                <el-table-column label="Nəticə" width="145" align="center">
                                    <template #default="scope">
                                        <span class="status-badge-pill" :class="getResultBadgeClass(scope.row.result)">
                                            <i :class="getResultBadgeIcon(scope.row.result)" class="badge-icon me-1"></i>
                                            <span>{{ scope.row.result }}</span>
                                        </span>
                                    </template>
                                </el-table-column>

                                <!-- Action: Delete (widened to 58px) -->
                                <el-table-column width="58" align="center">
                                    <template #default="scope">
                                        <el-button
                                            type="danger"
                                            style="padding: 0 !important;"
                                            :icon="Delete"
                                            class="edit-btn"
                                            @click="DeleteItem(scope.row)"
                                            text
                                        />
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal Dialog: Yeni / Redaktə Tapşırıq -->
    <el-dialog
        v-model="dialogVis"
        :title="isEditMode ? 'Tapşırığı redaktə et' : 'Yeni tapşırıq əlavə et'"
        modal-class="task-modal-box"
        :close-on-click-modal="false"
        :destroy-on-close="true"
        width="480px"
    >
        <div class="card-body p-1">
            <el-form label-position="top" :model="values" @submit.prevent>
                <div class="row g-3">
                    <div class="col-sm-6">
                        <el-form-item label="Saat / Vaxt" class="mb-1">
                            <el-time-picker v-model="values.taskDateObj" format="HH:mm" placeholder="Seçin" style="width: 100%" />
                        </el-form-item>
                    </div>
                    <div class="col-sm-6">
                        <el-form-item label="Format" class="mb-1">
                            <el-select v-model="values.online" placeholder="Format" style="width: 100%">
                                <el-option label="Online" value="online" />
                                <el-option label="Offline" value="offline" />
                            </el-select>
                        </el-form-item>
                    </div>
                    <div class="col-sm-6">
                        <el-form-item label="Tapşırıq növü" class="mb-1">
                            <el-select v-model="values.taskType" placeholder="Növü seçin" style="width: 100%">
                                <el-option label="Görüş" value="Görüş" />
                                <el-option label="Zəng" value="Zəng" />
                                <el-option label="Ərizə imzalama" value="Ərizə imzalama" />
                            </el-select>
                        </el-form-item>
                    </div>
                    <div class="col-sm-6">
                        <el-form-item label="Nəticə (Status)" class="mb-1">
                            <el-select v-model="values.result" placeholder="Status" style="width: 100%">
                                <el-option label="Gözləmədə" value="Gözləmədə" />
                                <el-option label="Tamamlandı" value="Tamamlandı" />
                                <el-option label="Rədd edildi" value="Rədd edildi" />
                            </el-select>
                        </el-form-item>
                    </div>
                    <div class="col-12">
                        <el-form-item label="Şəxs (Tam adı)" class="mb-1">
                            <el-input v-model="values.personName" placeholder="Məs. Əli Məmmədov" />
                        </el-form-item>
                    </div>
                    <div class="col-12">
                        <el-form-item label="Şirkət" class="mb-1">
                            <el-input v-model="values.companyName" placeholder="Məs. AzərTech" />
                        </el-form-item>
                    </div>
                    <div class="col-12">
                        <el-form-item label="Qeydlər" class="mb-0">
                            <el-input type="textarea" :rows="3" v-model="values.descr" placeholder="Tapşırıq haqqında əlavə qeydlər..." />
                        </el-form-item>
                    </div>
                </div>
            </el-form>
        </div>
        <template #footer>
            <div class="d-flex justify-content-end gap-2 pt-2 border-top">
                <button type="button" class="btn action-btn action-btn--muted" @click="dialogVis = false">
                    İmtina
                </button>
                <button type="button" class="btn action-btn action-btn--success" @click="saveTask">
                    <i class="bi bi-check-lg me-1"></i>
                    <span>{{ isEditMode ? 'Yenilə' : 'Daxil et' }}</span>
                </button>
            </div>
        </template>
    </el-dialog>
</template>

<script>
import { ElConfigProvider, ElMessage, ElMessageBox } from "element-plus";
import { Edit, Delete } from "@element-plus/icons-vue";
import azLocale from "element-plus/es/locale/lang/az";
import dayjs from "dayjs";
import "dayjs/locale/az";

export default {
    name: "CrmTaskMain",
    components: { ElConfigProvider },
    data() {
        return {
            Edit,
            Delete,
            day: new Date(),
            azLocale,
            dialogVis: false,
            isEditMode: false,
            editIndex: -1,
            loading: false,
            isRefreshing: false,
            filterValue: "",
            searchQuery: "",
            progress: {},
            values: {
                taskDate: "10:00",
                taskDateObj: new Date(),
                online: "online",
                taskType: "Görüş",
                result: "Gözləmədə",
                personName: "",
                companyName: "",
                descr: ""
            },
            dataTask: [
                { taskDate: '09:15', online: 'online', personName: 'Əli Məmmədov', companyName: 'AzərTech', descr: 'Müştəri ilə ilkin görüş', result: 'Tamamlandı', taskType: 'Görüş' },
                { taskDate: '10:30', online: 'online', personName: 'Aygün Quliyeva', companyName: 'GlobalPharm', descr: 'Qiymət təklifi göndərildi', result: 'Gözləmədə', taskType: 'Zəng' },
                { taskDate: '11:45', online: 'offline', personName: 'Rauf Əliyev', companyName: 'MediLine', descr: 'Telefon zəngi – məhsul təqdimatı', result: 'Tamamlandı', taskType: 'Zəng' },
                { taskDate: '12:20', online: 'online', personName: 'Leyla Hüseynova', companyName: 'BioHealth', descr: 'Əlavə endirim müzakirəsi', result: 'Gözləmədə', taskType: 'Görüş' },
                { taskDate: '13:10', online: 'online', personName: 'Zaur Əsqərov', companyName: 'PharmaCo', descr: 'Müştərinin şikayəti araşdırıldı', result: 'Tamamlandı', taskType: 'Zəng' },
                { taskDate: '14:05', online: 'offline', personName: 'İlkin Babaev', companyName: 'ClinicaPlus', descr: 'Təklif olunan müqavilə imzalanmadı', result: 'Rədd edildi', taskType: 'Ərizə imzalama' },
                { taskDate: '15:30', online: 'offline', personName: 'Nigar Məmmədova', companyName: 'AzMed', descr: 'Yeni məhsul haqqında məlumat verildi', result: 'Tamamlandı', taskType: 'Görüş' },
                { taskDate: '16:45', online: 'offline', personName: 'Kamran Həsənov', companyName: 'MediFarm', descr: 'Müştəri ilə təkrar zəng', result: 'Gözləmədə', taskType: 'Zəng' },
                { taskDate: '17:15', online: 'online', personName: 'Sevda Quliyeva', companyName: 'NeoClinic', descr: 'Qiymət razılaşdırılması', result: 'Tamamlandı', taskType: 'Ərizə imzalama' }, 
                { taskDate: '09:40', online: 'offline', personName: 'Tural Məmmədli', companyName: 'MedPoint', descr: 'Endirim təklifi rədd edildi', result: 'Rədd edildi', taskType: 'Ərizə imzalama' },
                { taskDate: '10:25', online: 'offline', personName: 'Aysel Həsənova', companyName: 'AzərLife', descr: 'Müştəri ilə müqavilə bağlandı', result: 'Tamamlandı', taskType: 'Ərizə imzalama' }, 
            ]
        };
    },
    mounted() {
        this.generateProgress();
    },
    computed: {
        formattedMonthYear() {
            return dayjs(this.day).locale('az').format('MMMM YYYY');
        },
        formattedSelectedDate() {
            return dayjs(this.day).locale('az').format('D MMMM YYYY, dddd');
        },
        totalCompleted() {
            return this.dataTask.filter(t => t.result === 'Tamamlandı').length;
        },
        totalPending() {
            return this.dataTask.filter(t => t.result === 'Gözləmədə').length;
        },
        totalRejected() {
            return this.dataTask.filter(t => t.result === 'Rədd edildi').length;
        },
        filteredTasks() {
            let list = this.dataTask;
            if (this.filterValue) {
                list = list.filter((t) => t.result === this.filterValue);
            }
            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase().trim();
                list = list.filter(t => 
                    (t.personName && t.personName.toLowerCase().includes(q)) ||
                    (t.companyName && t.companyName.toLowerCase().includes(q)) ||
                    (t.descr && t.descr.toLowerCase().includes(q)) ||
                    (t.taskType && t.taskType.toLowerCase().includes(q))
                );
            }
            return list;
        },
    },
    methods: {
        generateProgress() {
            const today = dayjs(this.day);
            const startOfMonth = today.startOf("month");
            const daysInMonth = today.daysInMonth();

            const newProgress = {};
            for (let d = 1; d <= daysInMonth; d++) {
                const dateStr = dayjs(startOfMonth).date(d).format("YYYY-MM-DD");
                if (dayjs(dateStr).isBefore(today, "day")) {
                    newProgress[dateStr] = Math.floor(Math.random() * 51) + 45;
                }
            }
            this.progress = newProgress;
        },
        isSelectedDay(dateStr) {
            return dayjs(this.day).format('YYYY-MM-DD') === dateStr;
        },
        isToday(dateStr) {
            return dayjs().format('YYYY-MM-DD') === dateStr;
        },
        getKpiClass(pct) {
            if (pct >= 70) return 'kpi--high';
            if (pct >= 55) return 'kpi--mid';
            return 'kpi--low';
        },
        prevMonth() {
            this.day = dayjs(this.day).subtract(1, 'month').toDate();
            this.generateProgress();
        },
        nextMonth() {
            this.day = dayjs(this.day).add(1, 'month').toDate();
            this.generateProgress();
        },
        goToday() {
            this.day = new Date();
            this.generateProgress();
        },
        getInitials(name) {
            if (!name) return "TP";
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return (parts[0][0] + parts[1][0]).toUpperCase();
        },
        getAvatarStyle(name) {
            const colors = [
                { bg: 'rgba(59, 130, 246, 0.14)', text: '#2563eb' },
                { bg: 'rgba(16, 185, 129, 0.14)', text: '#059669' },
                { bg: 'rgba(168, 85, 247, 0.14)', text: '#7c3aed' },
                { bg: 'rgba(245, 158, 11, 0.14)', text: '#d97706' },
                { bg: 'rgba(236, 72, 153, 0.14)', text: '#db2777' },
                { bg: 'rgba(20, 184, 166, 0.14)', text: '#0d9488' }
            ];
            let hash = 0;
            for (let i = 0; i < (name || "").length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            const idx = Math.abs(hash) % colors.length;
            return {
                backgroundColor: colors[idx].bg,
                color: colors[idx].text
            };
        },
        getTaskTypeIcon(type) {
            if (type === 'Görüş') return 'bi bi-people-fill text-primary';
            if (type === 'Zəng') return 'bi bi-telephone-fill text-info';
            return 'bi bi-file-earmark-text-fill text-warning';
        },
        getResultBadgeClass(result) {
            if (result === 'Tamamlandı') return 'badge--approved';
            if (result === 'Gözləmədə') return 'badge--in-progress';
            return 'badge--rejected';
        },
        getResultBadgeIcon(result) {
            if (result === 'Tamamlandı') return 'bi bi-check-circle-fill';
            if (result === 'Gözləmədə') return 'bi bi-hourglass-split';
            return 'bi bi-x-circle-fill';
        },
        refreshTasks() {
            this.isRefreshing = true;
            this.loading = true;
            setTimeout(() => {
                this.generateProgress();
                this.loading = false;
                this.isRefreshing = false;
                ElMessage({
                    message: "Tapşırıqlar yeniləndi",
                    type: "success",
                    duration: 2000
                });
            }, 450);
        },
        openNewDialog() {
            this.isEditMode = false;
            this.editIndex = -1;
            this.values = {
                taskDate: "10:00",
                taskDateObj: new Date(),
                online: "online",
                taskType: "Görüş",
                result: "Gözləmədə",
                personName: "",
                companyName: "",
                descr: ""
            };
            this.dialogVis = true;
        },
        editTask(row) {
            this.isEditMode = true;
            this.editIndex = this.dataTask.indexOf(row);
            this.values = {
                ...row,
                taskDateObj: new Date()
            };
            this.dialogVis = true;
        },
        saveTask() {
            if (!this.values.personName) {
                ElMessage.warning("Zəhmət olmasa şəxsin adını qeyd edin");
                return;
            }
            let timeStr = this.values.taskDate || "10:00";
            if (this.values.taskDateObj) {
                timeStr = dayjs(this.values.taskDateObj).format("HH:mm");
            }
            const taskData = {
                taskDate: timeStr,
                online: this.values.online || "online",
                personName: this.values.personName,
                companyName: this.values.companyName || "Daxili",
                descr: this.values.descr || "Qeyd yoxdur",
                result: this.values.result || "Gözləmədə",
                taskType: this.values.taskType || "Görüş"
            };

            if (this.isEditMode && this.editIndex >= 0) {
                this.dataTask.splice(this.editIndex, 1, taskData);
                ElMessage.success("Tapşırıq yeniləndi");
            } else {
                this.dataTask.unshift(taskData);
                ElMessage.success("Yeni tapşırıq əlavə edildi");
            }
            this.dialogVis = false;
        },
        DeleteItem(row) {
            ElMessageBox.confirm(
                `"${row.personName}" üzrə tapşırığı silmək istədiyinizdən əminsiniz?`,
                "Təsdiq",
                {
                    confirmButtonText: "Bəli, sil",
                    cancelButtonText: "İmtina",
                    type: "warning"
                }
            ).then(() => {
                const idx = this.dataTask.indexOf(row);
                if (idx > -1) {
                    this.dataTask.splice(idx, 1);
                    ElMessage.success("Tapşırıq uğurla silindi");
                }
            }).catch(() => {});
        }
    }
};
</script>

<style scoped lang="scss">
.task-calendar-card,
.task-table-card {
    border: 1px solid var(--app-border, #e2e8f0);
    border-radius: var(--app-radius, 12px);
    background: var(--app-surface, #ffffff);
}

.cal-header-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: rgba(11, 79, 156, 0.1);
    color: var(--bs-info, #0b4f9c);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;

    &--blue {
        background: rgba(59, 130, 246, 0.12);
        color: #2563eb;
    }
}

.cal-month-title {
    font-size: 0.95rem;
    color: var(--app-heading, #1e293b);
    text-transform: capitalize;
}

.cal-nav-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid var(--app-border, #e2e8f0);
    background: var(--app-surface-soft, #f8fafc);
    color: var(--app-heading, #334155);
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: var(--app-surface-elevated, #edf5fc);
        border-color: #9dc4e8;
        color: #0b4f9c;
    }
}

.cal-today-btn {
    height: 28px;
    padding: 0 8px;
    border-radius: 6px;
    border: 1px solid var(--app-border, #e2e8f0);
    background: var(--app-surface-soft, #f8fafc);
    color: var(--app-heading, #334155);
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: #0b4f9c;
        border-color: #0b4f9c;
        color: #ffffff;
    }
}

/* Custom Day Cell inside Calendar */
:deep(.el-calendar) {
    --el-calendar-border: 1px solid var(--app-border, #e2e8f0);
    background: transparent;

    .el-calendar__header {
        display: none !important;
    }

    .el-calendar__body {
        padding: 0 !important;
    }

    .el-calendar-table thead th {
        font-size: 0.76rem;
        font-weight: 700;
        color: var(--app-text, #64748b);
        padding: 8px 0;
        text-align: center;
        border-bottom: 1px solid var(--app-border, #e2e8f0);
    }

    .el-calendar-table .el-calendar-day {
        height: 52px !important;
        padding: 2px !important;
        transition: all 0.15s ease;

        &:hover {
            background: rgba(59, 130, 246, 0.06) !important;
        }
    }

    .el-calendar-table td.is-selected {
        background: transparent !important;
    }
}

.cal-cell-content {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    transition: all 0.15s ease;
    border: 1px solid transparent;

    &.is-selected-cell {
        background: rgba(11, 79, 156, 0.12) !important;
        border-color: var(--bs-info, #0b4f9c) !important;
        box-shadow: 0 0 0 1.5px rgba(11, 79, 156, 0.25) inset;

        .cal-day-number {
            font-weight: 800;
            color: var(--bs-info, #0b4f9c);
        }
    }

    &.is-today-cell:not(.is-selected-cell) {
        border-color: #94a3b8;
        background: rgba(148, 163, 184, 0.08);

        .cal-day-number {
            font-weight: 700;
        }
    }
}

.cal-day-number {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1;
    color: var(--app-heading, #1e293b);
}

.cal-kpi-chip {
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1;
    padding: 1px 5px;
    border-radius: 9999px;
    letter-spacing: -0.01em;

    &.kpi--high {
        background: rgba(16, 185, 129, 0.15);
        color: #059669;
    }
    &.kpi--mid {
        background: rgba(245, 158, 11, 0.18);
        color: #d97706;
    }
    &.kpi--low {
        background: rgba(239, 68, 68, 0.15);
        color: #dc2626;
    }
}

/* Selected Date Stats Summary */
.cal-selected-summary {
    margin-top: 10px;
    padding: 12px;
    border-radius: 8px;
    background: var(--app-surface-soft, #f8fafc);
    border: 1px solid var(--app-border, #e2e8f0);
}

.cal-summary-date {
    font-size: 0.84rem;
    color: var(--app-heading, #1e293b);
    text-transform: capitalize;
}

.cal-task-count-badge {
    font-size: 0.72rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 9999px;
    background: rgba(11, 79, 156, 0.12);
    color: var(--bs-info, #0b4f9c);
}

.cal-stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.cal-stat-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 6px 4px;
    border-radius: 6px;
    background: var(--app-surface, #ffffff);
    border: 1px solid var(--app-border, #e2e8f0);

    .stat-value {
        font-size: 1.05rem;
        font-weight: 800;
        line-height: 1.1;
    }

    .stat-label {
        font-size: 0.68rem;
        font-weight: 600;
        color: var(--app-text, #64748b);
        margin-top: 2px;
    }
}

/* Exact Pixel-Perfect Toolbar Line */
.task-toolbar-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    height: 38px;
    line-height: 1;

    .action-btn {
        height: 38px !important;
        min-height: 38px !important;
        max-height: 38px !important;
        box-sizing: border-box !important;
        margin: 0 !important;
        line-height: 36px !important;
        display: inline-flex !important;
        align-items: center !important;
        vertical-align: middle !important;
    }

    .toolbar-v-divider {
        height: 20px !important;
        margin: 0 4px !important;
        align-self: center !important;
    }
}

/* Modern Animated Status Rail - EXACT 38px height lock */
.modern-status-rail {
    display: inline-flex;
    align-items: center;
    height: 38px !important;
    min-height: 38px !important;
    max-height: 38px !important;
    box-sizing: border-box !important;
    background: var(--app-surface-soft, #f1f5f9);
    border: 1px solid var(--app-border, #e2e8f0);
    border-radius: 6px;
    padding: 3px;
    gap: 3px;
    margin: 0 !important;
    vertical-align: middle !important;
}

.status-rail-item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 30px !important;
    min-height: 30px !important;
    max-height: 30px !important;
    box-sizing: border-box !important;
    padding: 0 10px !important;
    border-radius: 4px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--app-text, #64748b);
    font-size: 0.8rem;
    font-weight: 650;
    line-height: 30px;
    cursor: pointer;
    white-space: nowrap;
    margin: 0 !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    .status-icon {
        font-size: 0.85rem;
        display: inline-flex;
        align-items: center;
        line-height: 1;
        transition: transform 0.25s ease;
    }

    span {
        display: inline-flex;
        align-items: center;
        line-height: 1;
    }

    .status-count-badge {
        font-size: 0.72rem;
        font-weight: 750;
        height: 18px;
        line-height: 18px;
        padding: 0 6px;
        border-radius: 9999px;
        background: rgba(100, 116, 139, 0.14);
        color: var(--app-heading, #334155);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
    }

    &:hover {
        color: var(--app-heading, #1e293b);
        background: rgba(255, 255, 255, 0.7);

        .status-icon {
            transform: scale(1.18);
        }

        .anim-spin-slow {
            animation: slowRotate 1.5s linear infinite;
        }

        .anim-pulse {
            animation: pulseIcon 0.8s ease infinite alternate;
        }

        .anim-shake {
            animation: shakeIcon 0.4s ease infinite alternate;
        }
    }

    &.is-active {
        background: var(--app-surface, #ffffff);
        color: var(--bs-info, #0b4f9c);
        border-color: var(--app-border, #cbd5e1);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

        .status-count-badge {
            background: rgba(11, 79, 156, 0.12);
            color: var(--bs-info, #0b4f9c);
        }

        &.status--success {
            color: #059669;
            .status-count-badge {
                background: rgba(16, 185, 129, 0.15);
                color: #059669;
            }
        }

        &.status--primary {
            color: #2563eb;
            .status-count-badge {
                background: rgba(59, 130, 246, 0.15);
                color: #2563eb;
            }
        }

        &.status--danger {
            color: #dc2626;
            .status-count-badge {
                background: rgba(239, 68, 68, 0.15);
                color: #dc2626;
            }
        }
    }
}

@keyframes slowRotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(180deg); }
}

@keyframes pulseIcon {
    0% { transform: scale(1); }
    100% { transform: scale(1.22); }
}

@keyframes shakeIcon {
    0% { transform: rotate(-8deg); }
    100% { transform: rotate(8deg); }
}

.task-search-box {
    min-width: 220px;

    :deep(.el-input__wrapper) {
        height: 34px !important;
        border-radius: 6px;
    }
}

.task-time-chip {
    font-family: var(--app-font-mono, monospace);
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--app-heading, #1e293b);
    padding: 3px 6px;
    background: var(--app-surface-soft, #f8fafc);
    border: 1px solid var(--app-border, #e2e8f0);
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
}

.person-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.76rem;
    font-weight: 750;
    flex-shrink: 0;
}

.person-name {
    font-size: 0.86rem;
    font-weight: 700;
    color: var(--app-heading, #1e293b);
    line-height: 1.25;
}

.company-name {
    font-size: 0.74rem;
    color: var(--app-text, #64748b);
    margin-top: 2px;
}

.task-note-text {
    font-size: 0.84rem;
    color: var(--app-heading, #334155);
    line-height: 1.35;
}

.task-type-label {
    font-size: 0.82rem;
    font-weight: 650;
    color: var(--app-heading, #1e293b);
}

.badge-status-dot {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.72rem;
    font-weight: 600;

    .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
    }

    &.online {
        color: #10b981;
        .dot {
            background: #10b981;
            box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
        }
    }

    &.offline {
        color: #94a3b8;
        .dot {
            background: #94a3b8;
        }
    }
}

/* Status Badge Pill */
.status-badge-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border-radius: 9999px;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    white-space: nowrap;

    .badge-icon {
        font-size: 0.78rem;
    }

    &.badge--approved {
        background: rgba(16, 185, 129, 0.12);
        color: #059669;
        border: 1px solid rgba(16, 185, 129, 0.35);
    }

    &.badge--in-progress {
        background: rgba(59, 130, 246, 0.12);
        color: #2563eb;
        border: 1px solid rgba(59, 130, 246, 0.35);
    }

    &.badge--rejected {
        background: rgba(239, 68, 68, 0.12);
        color: #dc2626;
        border: 1px solid rgba(239, 68, 68, 0.35);
    }
}

/* Table Action Buttons */
.task-action-btn {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.88rem;
    cursor: pointer;
    transition: all 0.15s ease;

    &.edit {
        color: var(--bs-info, #0b4f9c);
        &:hover {
            background: rgba(11, 79, 156, 0.12);
            border-color: rgba(11, 79, 156, 0.25);
        }
    }

    &.delete {
        color: #ef4444;
        &:hover {
            background: rgba(239, 68, 68, 0.12);
            border-color: rgba(239, 68, 68, 0.25);
        }
    }
}

.spin-anim {
    animation: spinRotate 0.8s linear infinite;
}

@keyframes spinRotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Dark Mode Tweaks */
[data-theme="dark"] {
    .task-calendar-card,
    .task-table-card {
        background: #182029;
        border-color: #2e3846;
    }

    .cal-header-icon {
        background: rgba(59, 130, 246, 0.2);
        color: #60a5fa;
    }

    .cal-nav-btn,
    .cal-today-btn {
        background: #1c2633;
        border-color: #2e3846;
        color: #e2e8f0;

        &:hover {
            background: #253344;
            border-color: #60a5fa;
            color: #93c5fd;
        }
    }

    :deep(.el-calendar) {
        --el-calendar-border: 1px solid #283544;

        .el-calendar-table thead th {
            color: #94a3b8;
            border-bottom-color: #2e3846;
        }

        .el-calendar-table .el-calendar-day {
            &:hover {
                background: rgba(59, 130, 246, 0.12) !important;
            }
        }
    }

    .cal-cell-content {
        &.is-selected-cell {
            background: rgba(59, 130, 246, 0.2) !important;
            border-color: #60a5fa !important;
            box-shadow: 0 0 12px rgba(59, 130, 246, 0.3) inset;

            .cal-day-number {
                color: #93c5fd;
            }
        }

        &.is-today-cell:not(.is-selected-cell) {
            border-color: #475569;
            background: rgba(255, 255, 255, 0.04);
        }
    }

    .cal-day-number {
        color: #e2e8f0;
    }

    .cal-selected-summary {
        background: #141b22;
        border-color: #283544;
    }

    .cal-summary-date {
        color: #f1f5f9;
    }

    .cal-stat-card {
        background: #182029;
        border-color: #283544;

        .stat-label {
            color: #94a3b8;
        }
    }

    .task-time-chip {
        background: #151b22;
        border-color: #2e3846;
        color: #e2e8f0;
    }

    .person-name {
        color: #f1f5f9;
    }

    .company-name {
        color: #94a3b8;
    }

    .task-note-text {
        color: #cbd5e1;
    }

    .task-type-label {
        color: #e2e8f0;
    }

    .status-badge-pill {
        &.badge--approved {
            background: rgba(16, 185, 129, 0.18);
            color: #34d399;
            border-color: rgba(52, 211, 153, 0.35);
        }
        &.badge--in-progress {
            background: rgba(59, 130, 246, 0.2);
            color: #93c5fd;
            border-color: rgba(96, 165, 250, 0.35);
        }
        &.badge--rejected {
            background: rgba(239, 68, 68, 0.2);
            color: #fca5a5;
            border-color: rgba(248, 113, 113, 0.35);
        }
    }

    .modern-status-rail {
        background: #141b22;
        border-color: #283544;
    }

    .status-rail-item {
        color: #94a3b8;

        &:hover {
            color: #f1f5f9;
            background: rgba(255, 255, 255, 0.06);
        }

        &.is-active {
            background: #1e2836;
            border-color: #3b4d61;
            color: #93c5fd;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);

            .status-count-badge {
                background: rgba(59, 130, 246, 0.22);
                color: #93c5fd;
            }

            &.status--success {
                color: #34d399;
                .status-count-badge {
                    background: rgba(16, 185, 129, 0.22);
                    color: #34d399;
                }
            }

            &.status--primary {
                color: #93c5fd;
                .status-count-badge {
                    background: rgba(59, 130, 246, 0.22);
                    color: #93c5fd;
                }
            }

            &.status--danger {
                color: #fca5a5;
                .status-count-badge {
                    background: rgba(239, 68, 68, 0.22);
                    color: #fca5a5;
                }
            }
        }
    }
}
</style>
